import React, { useContext, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import AppContext from 'Utils/AppContext';
import SourcePicker from './SourcePicker';
import { applyRestrictions, engineText } from './sourcePickerUtils';

const NO_ROWS = [];

/**
 * A form field that holds a data source choice and opens the shared SourcePicker in a dialog.
 * For forms where the choice is one input among several (a masking step's source and
 * destinations, a mask target). `value` is an id (single) or an array of ids (multiple);
 * `onChange(value, rows)` gets the same shape back. `clearable` lets the user empty it, and
 * `emptyLabel` says what empty means ("In place, no separate destination").
 */
function SourcePickerField({
  value,
  onChange,
  selection = 'single',
  sources,
  restrict = [],
  exclude = [],
  dialogTitle = 'Choose a data source',
  dialogSubtitle = null,
  verb,
  placeholder = 'No data source chosen',
  emptyLabel = null,
  clearable = false,
  disabled = false,
  names = {},
  testId = 'source-field',
}) {
  const ctx = useContext(AppContext) || {};
  const rowsIn = sources || ctx.connections || NO_ROWS;
  const multi = selection === 'multiple';
  const [open, setOpen] = useState(false);

  const ids = useMemo(() => {
    if (multi) return (value || []).map(String);
    return value == null || value === '' ? [] : [String(value)];
  }, [value, multi]);
  const byId = useMemo(() => {
    const m = {};
    rowsIn.forEach((s) => { if (s && s.id != null) m[String(s.id)] = s; });
    return m;
  }, [rowsIn]);
  // A chosen id the list does not hold (an edited step's old target) still shows by name.
  const nameOf = (id) => (byId[id] && byId[id].name) || names[id] || `#${id}`;

  // Hand back each row's own id (numeric on platform rows), not the picker's string key.
  const raw = (id) => (byId[id] ? byId[id].id : id);
  const emit = (nextIds, rows) => {
    if (multi) onChange(nextIds.map(raw), rows);
    else onChange(nextIds.length ? raw(nextIds[0]) : null, rows[0] || null);
  };
  const remove = (id) => {
    const next = ids.filter((x) => x !== id);
    emit(next, next.map((x) => byId[x]).filter(Boolean));
  };

  return (
    <>
      <div className={`sp-field${disabled ? ' sp-field--disabled' : ''}`} data-testid={testId}>
        {ids.length === 0 && <span className='sp-field-empty'>{emptyLabel || placeholder}</span>}
        {ids.map((id) => (
          <span key={id} className={`sp-field-chip${multi ? '' : ' sp-field-chip--single'}`} data-testid={`${testId}-value-${id}`}
            title={byId[id] ? engineText(byId[id]) : undefined}>
            {nameOf(id)}
            {(multi || clearable) && !disabled && (
              <button type='button' aria-label={`Remove ${nameOf(id)}`} onClick={() => remove(id)}
                data-testid={`${testId}-remove-${id}`}>×</button>
            )}
          </span>
        ))}
        <button type='button' className='sp-btn sp-btn--sm sp-field-btn' disabled={disabled} onClick={() => setOpen(true)}
          data-testid={`${testId}-open`}>
          {ids.length === 0 ? 'Choose…' : 'Change…'}
        </button>
      </div>
      <Modal show={open} onHide={() => setOpen(false)} size='xl' className='sp-modal' data-testid={`${testId}-dialog`}>
        <Modal.Header closeButton>
          <Modal.Title as='h2' style={{ fontSize: 18 }}>{dialogTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dialogSubtitle && <p className='sp-sub' style={{ padding: '12px 16px 0', margin: 0 }}>{dialogSubtitle}</p>}
          {open && (
            <SourcePicker
              sources={rowsIn}
              selection={selection}
              restrict={restrict}
              exclude={exclude}
              initialSelected={ids.filter((id) => byId[id])}
              verb={verb || (multi ? 'Use these' : 'Use this source')}
              maxHeight={Math.max(260, Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.55))}
              onConfirm={(rows) => {
                // Keep chosen ids the list no longer offers (the user did not deselect them).
                const offered = new Set(applyRestrictions(rowsIn, restrict, exclude).pool.map((s) => String(s.id)));
                const picked = rows.map((r) => String(r.id));
                // Earlier choices keep their place (and ones the list no longer offers are kept);
                // new ones follow in table order.
                const next = multi
                  ? ids.filter((id) => !offered.has(id) || picked.includes(id)).concat(picked.filter((id) => !ids.includes(id)))
                  : picked;
                emit(next, next.map((id) => byId[id]).filter(Boolean));
                setOpen(false);
              }}
              onCancel={() => setOpen(false)}
              testId={`${testId}-picker`}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default SourcePickerField;
